import type { ReactNode } from 'react'
import { AlertCircle, Info } from 'lucide-react'

import { cn } from '@/lib/utils'
import { errorMessage, isApiError } from '@/lib/api'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mx-auto flex w-full max-w-6xl flex-col gap-6 p-6', className)}>{children}</div>
}

/** A failed request, explained in reader terms (403s name the role they needed). */
export function ErrorNotice({ error, title }: { error: unknown; title?: string }) {
  const heading =
    title ??
    (isApiError(error)
      ? error.isNetwork
        ? 'Testbench host unreachable'
        : (error.problem?.title ?? `HTTP ${error.status}`)
      : 'Something went wrong')
  return (
    <Alert variant="destructive">
      <AlertCircle />
      <AlertTitle>{heading}</AlertTitle>
      <AlertDescription>{errorMessage(error)}</AlertDescription>
    </Alert>
  )
}

export function InfoNotice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Alert>
      <Info />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  )
}
